import { useEffect } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model"
import { WebsocketProvider } from "y-websocket"
import * as Y from "yjs"

import { CollaborationService } from "services/collaborationService"

interface UseYjsContentSyncProps {
  /** The TipTap editor instance */
  editor: TiptapEditor | null
  /** The current variant's Yjs document */
  currentDoc: Y.Doc | null
  /** Setter for the current Yjs document */
  setCurrentDoc: React.Dispatch<React.SetStateAction<Y.Doc | null>>
  /** Ref mirroring the current Yjs document */
  currentDocRef: React.RefObject<Y.Doc | null>
  /** Whether collaboration is enabled */
  collaborationEnabled: boolean
  /** Whether the collaboration session is initialized */
  isCollaborationReady: boolean
  /** The collaboration service instance */
  collaborationService: CollaborationService | null
  /** Returns the WebSocket provider for a variant */
  getProvider: (variantName: string) => WebsocketProvider | null
  /** Switches to a variant's pre-initialized Yjs document */
  switchToVariant: (variantName: string) => Y.Doc | null
  /** Current selected variant name */
  currentVariant: string
  /** The REST-fetched questionnaire content */
  questionnaire: string
  /** Queued variant switch, set by useCollaborativeSession */
  pendingVariantSwitchRef: React.RefObject<{ from: string; to: string } | null>
  /** Variants whose Yjs docs have been populated */
  populatedVariantsRef: React.RefObject<Set<string>>
}

/**
 * useYjsContentSync hosts the two editor-dependent Yjs effects:
 * populating the Yjs document once the provider has synced, and processing
 * queued variant switches. Both need the live editor instance, so they run
 * after useEditor — unlike the session lifecycle in useCollaborativeSession.
 */
export const useYjsContentSync = ({
  editor,
  currentDoc,
  setCurrentDoc,
  currentDocRef,
  collaborationEnabled,
  isCollaborationReady,
  collaborationService,
  getProvider,
  switchToVariant,
  currentVariant,
  questionnaire,
  pendingVariantSwitchRef,
  populatedVariantsRef,
}: UseYjsContentSyncProps): void => {
  // Populate Yjs document when editor is ready AND provider has synced.
  // Content is dispatched via a ProseMirror transaction so y-prosemirror
  // emits a minimal diff to Yjs (setContent would pile new ops on top).
  useEffect(() => {
    if (
      !editor ||
      !currentDoc ||
      !collaborationService ||
      !collaborationEnabled
    ) {
      return
    }

    const variantKey = currentVariant === "" ? "master" : currentVariant
    const provider = getProvider(variantKey)

    if (!provider) {
      return
    }

    // Provider "sync" events can fire long after this effect's editor was
    // torn down (tab restore, variant switch). A destroyed editor has no
    // schema, and DOMParser.fromSchema(null) crashes the whole route — so
    // every async entry point bails when cancelled/destroyed, and every
    // sync listener registered here is removed on cleanup.
    let cancelled = false
    const syncHandlers: Array<(isSynced: boolean) => void> = []
    const onProviderSync = (handler: (isSynced: boolean) => void) => {
      syncHandlers.push(handler)
      provider.on("sync", handler)
    }

    const checkAndPopulate = () => {
      if (cancelled || editor.isDestroyed) return
      const editorIsEmpty = editor.getHTML() === "<p></p>"
      const hasRemote = collaborationService.hasRemoteContent(variantKey)

      const replaceEditorContent = (html: string) => {
        if (cancelled || editor.isDestroyed) return
        const element = document.createElement("div")
        element.innerHTML = html
        const newDoc = ProseMirrorDOMParser.fromSchema(editor.schema).parse(
          element,
        )
        editor.view.dispatch(
          editor.state.tr.replaceWith(
            0,
            editor.state.doc.content.size,
            newDoc.content,
          ),
        )
      }

      if (
        editorIsEmpty &&
        !hasRemote &&
        questionnaire &&
        questionnaire !== "<p></p>"
      ) {
        // No Yjs content — populate from REST-fetched questionnaire.
        replaceEditorContent(questionnaire)
      } else if (hasRemote && collaborationService.isBackendNewer(variantKey)) {
        // Yjs has stale content — an HTTP save happened while WebSocket
        // was disconnected. Disconnect, ask the server to clear the stale
        // room (only if 0 clients), clear local Yjs fragment, reconnect,
        // and replace content on sync. clearYjsFragment is internally
        // guarded by awareness so it skips when other clients are connected.
        if (questionnaire && questionnaire !== "<p></p>") {
          collaborationService.clearPendingSave(variantKey)
          provider.disconnect()
          collaborationService.clearStaleRoom(variantKey).then(() => {
            collaborationService.clearYjsFragment(variantKey)

            const handleReconnectSync = (isSynced: boolean) => {
              if (isSynced) {
                replaceEditorContent(questionnaire)
                provider.off("sync", handleReconnectSync)
              }
            }
            onProviderSync(handleReconnectSync)
            provider.connect()
          })
        }
      }
      // Otherwise Yjs has current content, use it as-is
    }

    if (provider.synced) {
      checkAndPopulate()
    } else {
      const handleSync = (isSynced: boolean) => {
        if (isSynced) {
          checkAndPopulate()
          provider.off("sync", handleSync)
        }
      }
      onProviderSync(handleSync)
    }

    return () => {
      cancelled = true
      syncHandlers.forEach((handler) => provider.off("sync", handler))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, currentDoc])

  // Process pending variant switch - switch to existing pre-initialized Yjs doc
  // All docs are already initialized, we just switch between them
  useEffect(() => {
    if (!editor || !isCollaborationReady || !collaborationEnabled) {
      return
    }

    // Check if there's a pending variant switch
    const pending = pendingVariantSwitchRef.current
    if (!pending) {
      return
    }

    // Clear the pending switch
    pendingVariantSwitchRef.current = null

    // Switch to the existing variant's Yjs document (already initialized)
    const variantName = pending.to === "" ? "master" : pending.to

    // Clear the populated flag for the new variant to trigger population
    populatedVariantsRef.current.delete(variantName)

    const doc = switchToVariant(variantName)
    if (doc) {
      currentDocRef.current = doc
      setCurrentDoc(doc)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dep array kept verbatim from Editor.component; the ref and setter props are stable
  }, [
    currentVariant,
    editor,
    isCollaborationReady,
    collaborationEnabled,
    switchToVariant,
  ])
}
